"use client";

import { NFONE_SUPPORT_EMAIL } from "@newform/constants";
import { useTranslations } from "next-intl";
import { type ReactNode, useRef } from "react";
import { Alert, AlertDescription } from "@/components/ui/design-system/alert";
import { Body, Display, Rail } from "@/components/ui/design-system/text";
import { AdRevealBackground } from "./adReveal/AdRevealBackground";

/**
 * The page every creator sign-in screen sits on: the wordmark at the top, one
 * centred column, and the way to reach a person at the foot.
 *
 * Shared by the sign-in form and by every screen the auth gate stops someone
 * at (no profile, wrong invitation, a program that isn't available). Those
 * used to be a small bordered card with underlined links, which read as a
 * different product the moment the code was accepted.
 */
export function CreatorAuthFrame({
  children,
  background,
  banner,
  id,
}: {
  children: ReactNode;
  /** Names the page for a sheet's depth effect: `DEPTH_ROOT_ID`. */
  id?: string;
  /** Painted behind everything else, e.g. the sign-in page's ad wall. */
  background?: ReactNode;
  /** One line above the wordmark, e.g. the sign-in page's App Store pill. */
  banner?: ReactNode;
}) {
  const t = useTranslations("authFrame");
  return (
    <main
      id={id}
      className="relative flex min-h-screen flex-col overflow-hidden bg-nf-ds-bg px-nf-ds-4 py-nf-ds-16 text-nf-ds-fg"
    >
      {/* First child, so everything after it paints above without needing a
          z-index on each piece. */}
      {background}

      {banner && (
        <div className="relative flex justify-center pb-nf-ds-6">{banner}</div>
      )}

      {/*
        The wordmark sits at the top of the screen rather than above the
        headline. In the block it read as an eyebrow — a label for the
        sentence under it — when it is the product's name and belongs to the
        page.

        The same treatment as the portal's TopBar and SideRail: `NewForm` in
        the Swear wordmark face, `One` as a brand-coloured rail beside it, so
        signing in and being inside read as one product. `NewForm` takes the
        foreground role rather than a literal white, so it stays legible if
        this screen is ever seen on the light ground.
      */}
      <div className="relative flex justify-center pb-nf-ds-8">
        <span className="flex min-w-0 items-baseline gap-nf-ds-2 whitespace-nowrap text-nf-ds-fg">
          <span className="font-swear text-nf-ds-2xl leading-none tracking-[-0.01em]">
            NewForm
          </span>
          <Rail className="text-nf-ds-brand">One</Rail>
        </span>
      </div>
      <div className="relative mx-auto flex w-full min-w-0 max-w-md flex-1 flex-col justify-center">
        {children}
      </div>

      <footer className="relative mx-auto w-full max-w-md pt-nf-ds-6">
        <Body muted className="text-center text-nf-ds-sm">
          {/* One sentence with the link inside it, so a translation can move
              the link to wherever its grammar puts it. */}
          {t.rich("needHelp", {
            link: (chunks) => (
              <a
                className="text-nf-ds-fg underline decoration-nf-ds-fg/30 underline-offset-2"
                href={`mailto:${NFONE_SUPPORT_EMAIL}`}
              >
                {chunks}
              </a>
            ),
          })}
        </Body>
      </footer>
    </main>
  );
}

/**
 * A screen that stops someone on the way in (the auth gate's dead ends, the
 * application's closed and sent states), laid out like the sign-in page: a
 * centred headline, one muted paragraph, and the ways forward stacked full
 * width beneath it.
 */
export function CreatorAuthMessage({
  title,
  body,
  error,
  wall = true,
  children,
}: {
  title: string;
  body?: ReactNode;
  error?: string | null;
  /**
   * The ad wall behind the copy. On for sign-in's dead ends, which belong to
   * the sign-in page; off for the application's, which belong to a form.
   */
  wall?: boolean;
  /** The actions, first one filled. */
  children?: ReactNode;
}) {
  const guardRef = useRef<HTMLDivElement>(null);
  return (
    <CreatorAuthFrame
      background={wall ? <AdRevealBackground guardRef={guardRef} /> : null}
    >
      {/* Guarded like the sign-in form, so the wall dims behind the copy. */}
      <div ref={guardRef} className="flex flex-col gap-nf-ds-6">
        <header className="flex min-w-0 flex-col items-center gap-nf-ds-2 text-center">
          <Display as="h1" className="text-balance">
            {title}
          </Display>
          {body && (
            <Body muted className="text-pretty">
              {body}
            </Body>
          )}
        </header>
        {children && (
          <div className="flex w-full flex-col gap-nf-ds-3">{children}</div>
        )}
        {error && (
          <Alert variant="err" role="alert" className="w-full">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
    </CreatorAuthFrame>
  );
}
